/**
 * A3c (D-158) — Adress-Subgrid im Zeilen-Detail einer gebundenen Kontakte-Liste.
 * Die Zeilen-ID der Kontakte-Systemliste IST die contact_id; hier werden die
 * Adressen des Kontakts gelistet, angelegt, feldweise editiert (Autosave beim
 * Verlassen des Feldes) und gelöscht — CRUD über die A3a-CRM-Endpunkte.
 */

import { useCallback, useEffect, useState } from "react";

import { ryfeApi } from "../api";

import "./ryfe.scss";

import type { Address } from "../api";

// Die editierbaren Felder einer Adresse (kind zuerst; Notizen zuletzt).
const FIELDS: { key: keyof Address; label: string }[] = [
  { key: "kind", label: "Art" },
  { key: "street", label: "Straße" },
  { key: "zip", label: "PLZ" },
  { key: "city", label: "Ort" },
  { key: "country", label: "Land" },
  { key: "notes", label: "Notizen" },
];

export const ContactAddresses = ({
  wsId,
  contactId,
}: {
  wsId: string;
  contactId: string;
}) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await ryfeApi.listContactAddresses(wsId, contactId);
      setAddresses(res.addresses);
    } catch {
      setAddresses([]);
    }
  }, [wsId, contactId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const add = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await ryfeApi.addContactAddress(wsId, contactId, { kind: "main" });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  // Feldweiser Autosave: nur schreiben, wenn sich der Wert wirklich geändert hat.
  const saveField = async (a: Address, key: keyof Address, value: string) => {
    if (String(a[key] ?? "") === value) {
      return;
    }
    await ryfeApi.updateAddress(wsId, a.id, { [key]: value });
    await reload();
  };

  const remove = async (a: Address) => {
    await ryfeApi.deleteAddress(wsId, a.id);
    await reload();
  };

  return (
    <div className="ryfe-addresses" data-testid="contact-addresses">
      <span className="ryfe-elmenu__head">ADRESSEN</span>
      <ul className="ryfe-addresses__list">
        {addresses.map((a) => (
          <li key={a.id} className="ryfe-addresses__item">
            <div className="ryfe-addresses__fields">
              {FIELDS.map((f) => (
                <label key={f.key} className="ryfe-addresses__field">
                  <span className="ryfe-detail__k">{f.label}</span>
                  <input
                    className="ryfe-field-input"
                    defaultValue={String(a[f.key] ?? "")}
                    data-testid={`address-${f.key}-${a.id}`}
                    onBlur={(e) => void saveField(a, f.key, e.target.value)}
                  />
                </label>
              ))}
            </div>
            <button
              className="ryfe-btn ryfe-btn--ghost"
              title="Adresse löschen"
              data-testid={`address-remove-${a.id}`}
              onClick={() => void remove(a)}
            >
              ×
            </button>
          </li>
        ))}
        {addresses.length === 0 && (
          <li className="ryfe-muted">Noch keine Adressen.</li>
        )}
      </ul>
      <button
        className="ryfe-btn ryfe-btn--primary"
        disabled={busy}
        data-testid="address-add"
        onClick={() => void add()}
      >
        + Adresse
      </button>
    </div>
  );
};
